import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Button, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { MdiIcon, Table, WaitDialog } from '@components';
import { mdiPencil } from '@mdi/js';
import { useModal } from 'mui-modal-provider';
import moment from 'moment';
const RhAvaliacaoListView = ({ $state, RhService, UIService }) => {
    const loadParams = () => {
        const sortBy = $state.params.sortBy;
        if (sortBy == undefined) {
            return [];
        }
        const o = [];
        sortBy.forEach(r => {
            const s = r.split(':');
            o.push({ id: s[0], desc: s[1] == 'DESC' });
        });
        return o;
    };
    const { showModal } = useModal();
    const [data, setData] = useState(); // dados dos clientes
    const [loading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [error, setError] = useState(false);
    const [filter, setFilter] = useState([]); // filtros a aplicar na tabela
    // exemplo -> [{ id: 'nome', value: 'Pedro', type: 'contains' }, { id: 'email', value: 'gmail', type: 'contains' }]
    const [sort, setSort] = useState(() => loadParams()); // ordenação da tabela
    // exemplo -> [{id: 'ultimoContacto', desc: false}]
    const [options, setOptions] = useState({
        filter: [], // exemplo -> [{name: 'nome', type: 'TEXT'}, {name: 'ultimoContacto', type: 'DATE', options: {format: 'YYYY-MM-DD'}}, ...]
        sort: [] // exemplo -> ['id', 'nome', 'ultimoContacto', 'contactoTelefonico', 'nif', 'email', 'morada', 'localidade']
    });
    const [pagination, setPagination] = useState({
        pageIndex: $state.params.page - 1 || 0,
        pageSize: $state.params.limit || 20
    }); // paginação exemplo -> {pagIndex: 2, pageSize: 30}
    const [estados, setEstados] = useState([]);
    const [ciclos, setCiclos] = useState([]);
    const [avaliacoesQualitativas, setAvaliacoesQualitativas] = useState([]);
    // Filtragem dentro de cada coluna
    // Modo de filtro (igual a, contém, começa com... )
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            default:
                return '$eq';
        }
    };
    const columns = useMemo(() => [
        {
            accessorKey: 'Avaliador.name',
            header: 'Avaliador',
            size: 200
        },
        {
            accessorKey: 'Funcionario.name',
            header: 'Avaliado',
            size: 200
        },
        {
            accessorKey: 'Funcionario.numeroMecanografico',
            header: 'Nº do Avaliado',
            size: 200
        },
        {
            accessorKey: 'dataConclusao',
            header: 'Data de Conclusão',
            size: 200,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                if (value !== null)
                    return moment(value).format('DD/MM/YYYY HH:mm');
                return '-';
            }
        },
        {
            accessorKey: 'Estado.id',
            exportKey: 'Estado.designacao',
            header: 'Estado',
            size: 200,
            filterSelectOptions: estados.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            Cell: ({ row }) => row.original?.Estado?.designacao
        },
        {
            accessorKey: 'Ciclo.id',
            exportKey: 'Ciclo.designacao',
            header: 'Ciclo',
            size: 200,
            filterSelectOptions: ciclos.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            Cell: ({ row }) => row.original?.Ciclo?.designacao
        },
        {
            accessorKey: 'AvaliacaoQualitativa.id',
            exportKey: 'AvaliacaoQualitativa.designacao',
            header: 'Classificação',
            size: 200,
            filterSelectOptions: avaliacoesQualitativas.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            Cell: ({ row }) => row.original?.AvaliacaoQualitativa?.designacao
        }
    ], [estados, ciclos, avaliacoesQualitativas]);
    const initialFetch = async () => {
        if (loading) {
            const estadosAvaliacao = await RhService.getEstados();
            setEstados(estadosAvaliacao);
            const ciclosAvaliacao = await RhService.getCiclos();
            setCiclos(ciclosAvaliacao);
            const avaliacoesQualitativas = await RhService.getClassificacoes();
            setAvaliacoesQualitativas(avaliacoesQualitativas);
            // opt -> vai buscar os campos filtraveis
            const opt = await RhService.options();
            setOptions(opt);
            setLoading(false);
        }
    };
    const fetchData = async () => {
        setError(false);
        try {
            let filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            let sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            // aux vai buscar os dados consoante os filtros
            // exemplo -> { limit: 0, page: 20, filter[{nome: '$ilike:Pedro' }, ...], sortBy:['ultimoContacto:ASC',...]}
            const aux = await RhService.findAll({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            });
            // Update URL
            $state.go($state.current.name, {
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                sortBy: sorter,
                filter: Object.keys(filter).length > 0 ? JSON.stringify(filters) : undefined
            }, {
                notify: false,
                reload: false,
                location: 'replace',
                inherit: true
            });
            setData(aux);
        }
        catch (e) {
            console.log(e);
            setError(true);
        }
        setRefetching(false);
    };
    const excelGen = async (id, array) => {
        const wait = showModal(WaitDialog, { title: 'Por favor aguarde...' });
        let filename = `avaliacao-${id ? 'selecao' : 'exportacao'}-${moment().format('YYYY-MM-DD-HH-mm-ss')}.xlsx`;
        let filters = {};
        filter.forEach(r => {
            filters[r.id] = `${getType(r.type)}:${r.value}`;
        });
        if (id) {
            filters['id'] = `$in:${array.join(',')}`;
        }
        else {
            // Aplica filtro temporal
        }
        let sorter = [];
        sort.forEach(r => {
            sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
        });
        try {
            let o = await RhService.exportProcessos({
                columns: columns.map(r => {
                    return {
                        accessorKey: r.exportKey ?? r.accessorKey,
                        header: r.header
                    };
                }),
                filter: filters,
                sortBy: sorter
            });
            if (o) {
                // Decode Base64 and convert to a Blob
                const binary = atob(o.$data);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    bytes[i] = binary.charCodeAt(i);
                }
                // Create a Blob and save it as an Excel file
                const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                link.click();
            }
        }
        catch (e) {
            if (e && e.data) {
                UIService.addToast(e.data.message);
            }
            else {
                UIService.addToast('Ocorreu um erro ao exportar dados!');
            }
        }
        wait.destroy();
    };
    const onCellView = (id) => {
        $state.go('app.rh.avaliacoes.formEdit', { id: id });
    };
    useEffect(() => {
        initialFetch();
        if (!refetching) {
            setRefetching(true);
            fetchData();
        }
    }, [pagination, filter, sort]);
    return (<Box sx={{ pl: 1, pr: 1 }}>
      <Breadcrumbs aria-label='breadcrumb' sx={{ mt: 2 }}>
        <Link underline='hover' color='inherit' href='/'>
          <Typography variant='body2'>RH</Typography>
        </Link>
        <Link underline='hover' color='inherit'>
          <Typography variant='body2'>Avaliações</Typography>
        </Link>
      </Breadcrumbs>

      <Typography variant='h4' sx={{ color: 'black', mt: 2 }}>
        Avaliações
      </Typography>

      <Box sx={{
            mt: 2,
            borderRadius: '15px',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
        <Table onDoubleClick={row => {
            onCellView(row.id);
        }} storageKey={$state.current.name} data={data} columns={columns} initial={{
            sort: sort,
            filter: filter
        }} isLoading={refetching} options={options} onExport={async () => {
            excelGen(false, []);
        }} selectActions={selected => {
            return [
                <Button onClick={async () => {
                        if (selected.length > 0) {
                            excelGen(true, selected);
                        }
                    }}>
                Exportar
              </Button>
            ];
        }} selectable={row => {
            return true;
        }} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} 
    //rowActionSize={150}
    actionItems={({ row }) => [
            <Box display='flex' gap={1} key={row.original.id}>
              <Tooltip title='Editar Avaliação' placement='left'>
                <IconButton color='primary' onClick={() => onCellView(row.id)}>
                  <MdiIcon path={mdiPencil}/>
                </IconButton>
              </Tooltip>
            </Box>
        ]}></Table>
      </Box>
      {/* <Fab
          aria-label='add'
          color='error'
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000
          }}>
          <IconButton onClick={() => window.open($state.href('app.rh.avaliacoes.formCreate'))}>
            <MdiIcon path={mdiPlus} sx={{ color: '#ffffff' }} />
          </IconButton>
        </Fab> */}
    </Box>);
};
export default RhAvaliacaoListView;
